export const SIDEBAR_NAV_ANNOUNCEMENT_DISMISSED_KEY =
  'sidebar_nav_announcement_dismissed'

// Both surfaces that can dismiss the announcement (the desktop sidebar
// coachmark and the mobile dashboard card) share this one key, so dismissing on
// either dismisses both. No subscription needed: each surface is gated on
// viewport, so the only way to cross from one to the other is a width change,
// and that re-render re-runs this read.
export const isSidebarNavAnnouncementDismissed = (): boolean => {
  try {
    return (
      window.localStorage.getItem(SIDEBAR_NAV_ANNOUNCEMENT_DISMISSED_KEY) ===
      'true'
    )
  } catch {
    return false
  }
}

export const dismissSidebarNavAnnouncement = (): void => {
  try {
    window.localStorage.setItem(SIDEBAR_NAV_ANNOUNCEMENT_DISMISSED_KEY, 'true')
  } catch {
    // storage unavailable (private mode / disabled) — dismissal is best-effort,
    // so each surface keeps its own session flag as the fallback.
  }
}

export const SIDEBAR_NAV_ANNOUNCEMENT_SHOW_COUNT_KEY =
  'sidebar_nav_announcement_show_count'

export const getSidebarNavAnnouncementShowCount = (): number => {
  try {
    const stored = Number(
      window.localStorage.getItem(SIDEBAR_NAV_ANNOUNCEMENT_SHOW_COUNT_KEY)
    )
    return Number.isInteger(stored) && stored > 0 ? stored : 0
  } catch {
    return 0
  }
}

// Records this display and returns the count *including* it, so the caller can
// stamp `show_count` onto the events it fires for this exposure. Persisted
// rather than in-memory so we can tell how many times a user was re-shown the
// tour across sessions before finishing or closing it.
export const recordSidebarNavAnnouncementShown = (): number => {
  const showCount = getSidebarNavAnnouncementShowCount() + 1
  try {
    window.localStorage.setItem(
      SIDEBAR_NAV_ANNOUNCEMENT_SHOW_COUNT_KEY,
      String(showCount)
    )
  } catch {
    // storage unavailable (private mode / disabled) — same best-effort as dismissal
  }
  return showCount
}

export const SIDEBAR_NAV_ANNOUNCEMENT_TEMPORARY_CLOSE_COUNT_KEY =
  'sidebar_nav_announcement_temporary_close_count'

// Counts "Dismiss" presses, the unpersisted close. Distinct from the dismissed
// key above, which records that someone is done with the announcement.
export const getSidebarNavAnnouncementTemporaryCloseCount = (): number => {
  try {
    const stored = Number(
      window.localStorage.getItem(
        SIDEBAR_NAV_ANNOUNCEMENT_TEMPORARY_CLOSE_COUNT_KEY
      )
    )
    return Number.isInteger(stored) && stored > 0 ? stored : 0
  } catch {
    return 0
  }
}

// Returns the count including this press. Persisted because closing is not, so
// the tally has to survive the visits the tour keeps coming back across.
export const recordSidebarNavAnnouncementTemporarilyClosed = (): number => {
  const temporaryCloseCount = getSidebarNavAnnouncementTemporaryCloseCount() + 1
  try {
    window.localStorage.setItem(
      SIDEBAR_NAV_ANNOUNCEMENT_TEMPORARY_CLOSE_COUNT_KEY,
      String(temporaryCloseCount)
    )
  } catch {
    // storage unavailable (private mode / disabled), same best-effort as dismissal
  }
  return temporaryCloseCount
}
